import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Clock, Users, Calendar, ChevronRight, Menu, X, LogIn, Search, ArrowUpRight, Award, Trophy, Sparkles } from 'lucide-react';
import axios from 'axios';
import ShowcaseStrip from '@/components/ShowcaseStrip';
import BottomNav from '@/components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "/logo.jpeg";

const fadeUp = { hidden: { opacity: 0, y: 28 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] } }) };
const stagger = { visible: { transition: { staggerChildren: 0.07 } } };

function Header() {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { const h = () => setScrolled(window.scrollY > 40); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'glass py-2.5' : 'bg-transparent py-5'}`} data-testid="main-header">
      <div className="max-w-7xl mx-auto px-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img loading="lazy" decoding="async" src={LOGO} alt="Musicoul" className="w-9 h-9 rounded-full ring-2 ring-[#8B5CF6]/30 group-hover:ring-[#8B5CF6]/50 transition-all" />
          <span className="font-heading text-lg tracking-[0.15em] text-white">MUSICOUL</span>
        </Link>
        <nav className="hidden md:flex items-center gap-10">
          {['About', 'Courses'].map(item => (
            <a key={item} href={`#${item.toLowerCase()}`} className="text-[#B0B0BE] hover:text-[#F59E0B] transition-colors text-[13px] tracking-wider uppercase">{item}</a>
          ))}
          <Link to="/branches" className="text-[#B0B0BE] hover:text-[#F59E0B] transition-colors text-[13px] tracking-wider uppercase" data-testid="header-branches-link">Branches</Link>
          <Link to="/events" className="text-[#B0B0BE] hover:text-[#F59E0B] transition-colors text-[13px] tracking-wider uppercase" data-testid="header-events-link">Events</Link>
          <button onClick={() => navigate(user ? '/dashboard' : '/auth')}
            className="bg-gradient-to-r from-[#EF4444] to-[#8B5CF6] text-white px-7 py-2.5 text-[12px] font-bold tracking-[0.15em] hover:shadow-[0_0_25px_rgba(139,92,246,0.4)] transition-all rounded-lg"
            data-testid="nav-login-btn">
            {user ? 'DASHBOARD' : <><LogIn size={13} className="inline mr-1.5" /> LOGIN</>}
          </button>
        </nav>
        <button className="md:hidden text-[#F59E0B]" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <AnimatePresence>{menuOpen && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="md:hidden overflow-hidden">
          <div className="glass px-5 py-5 space-y-4">
            {['About', 'Courses'].map(item => (<a key={item} href={`#${item.toLowerCase()}`} className="block text-[#B0B0BE] text-sm" onClick={() => setMenuOpen(false)}>{item}</a>))}
            <Link to="/branches" className="block text-[#B0B0BE] text-sm" data-testid="mobile-branches-link">Branches</Link>
            <Link to="/events" className="block text-[#B0B0BE] text-sm" data-testid="mobile-events-link">Events</Link>
            <button onClick={() => { navigate(user ? '/dashboard' : '/auth'); setMenuOpen(false); }} className="w-full bg-gradient-to-r from-[#EF4444] to-[#8B5CF6] text-white py-2.5 text-sm font-bold rounded-lg">{user ? 'Dashboard' : 'Login'}</button>
          </div>
        </motion.div>
      )}</AnimatePresence>
    </header>
  );
}

function HeroSection() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 80]);

  return (
    <section ref={ref} className="relative min-h-[100vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-[#0A0A14]">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(139,92,246,0.12)_0%,transparent_60%)]" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(239,68,68,0.08)_0%,transparent_60%)]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[radial-gradient(circle,rgba(139,92,246,0.05)_0%,transparent_50%)]" />
        <motion.div animate={{ opacity: [0.06, 0.15, 0.06] }} transition={{ repeat: Infinity, duration: 4 }}
          className="absolute top-1/3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B5CF6]/20 to-transparent" />
        <motion.div animate={{ opacity: [0.04, 0.1, 0.04] }} transition={{ repeat: Infinity, duration: 5, delay: 1 }}
          className="absolute bottom-1/3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#EF4444]/15 to-transparent" />
      </div>

      <motion.div style={{ opacity, y }} className="relative z-10 text-center px-5 max-w-3xl mx-auto">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.div variants={fadeUp} custom={0} className="mb-6 sm:mb-10">
            <div className="relative inline-block">
              <img loading="lazy" decoding="async" src={LOGO} alt="Musicoul" className="w-16 h-16 sm:w-24 sm:h-24 mx-auto rounded-full" />
              <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }} transition={{ repeat: Infinity, duration: 3 }}
                className="absolute inset-0 rounded-full bg-gradient-to-r from-[#EF4444]/20 to-[#8B5CF6]/20 blur-xl" />
            </div>
          </motion.div>
          <motion.p variants={fadeUp} custom={1} className="text-[9px] sm:text-[11px] font-bold uppercase tracking-[0.3em] sm:tracking-[0.5em] text-[#F59E0B] mb-4 sm:mb-6">Premier Music Institution</motion.p>
          <motion.h1 variants={fadeUp} custom={2} className="font-heading text-3xl sm:text-5xl lg:text-7xl font-normal mb-3 sm:mb-4 leading-[1.15]">
            <span className="text-gold-gradient">Where Music</span>
          </motion.h1>
          <motion.h1 variants={fadeUp} custom={3} className="font-heading text-3xl sm:text-5xl lg:text-7xl font-normal text-white mb-5 sm:mb-8 leading-[1.15]">Meets the Soul</motion.h1>
          <motion.p variants={fadeUp} custom={4} className="text-[13px] sm:text-[15px] text-[#B0B0BE] font-light mb-8 sm:mb-14 max-w-lg mx-auto leading-relaxed px-2">
            Master Indian classical, Western, and contemporary music with personalized training from virtuoso musicians.
          </motion.p>
          <motion.div variants={fadeUp} custom={5} className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
            <button onClick={() => document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-[#EF4444] to-[#8B5CF6] text-white px-6 sm:px-10 py-3 sm:py-4 text-[12px] sm:text-[13px] font-bold tracking-[0.1em] sm:tracking-[0.15em] hover:shadow-[0_0_35px_rgba(139,92,246,0.4)] transition-all rounded-lg group"
              data-testid="explore-courses-btn">
              EXPLORE COURSES <ChevronRight size={14} className="inline ml-1 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigate('/auth')}
              className="border border-[#8B5CF6]/30 text-[#A78BFA] px-6 sm:px-10 py-3 sm:py-4 text-[12px] sm:text-[13px] tracking-[0.1em] sm:tracking-[0.15em] hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]/50 transition-all rounded-lg"
              data-testid="join-now-btn">JOIN NOW</button>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}

function AboutSection() {
  const stats = [
    { label: 'Students Trained', value: '2000+', color: '#EF4444' },
    { label: 'Expert Faculty', value: '15+', color: '#8B5CF6' },
    { label: 'Courses Offered', value: '10+', color: '#10B981' },
    { label: 'Years of Legacy', value: '12+', color: '#A78BFA' },
  ];
  return (
    <section id="about" className="py-16 sm:py-32 px-4 sm:px-5" data-testid="about-section">
      <div className="max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
            <div className="w-10 h-0.5 bg-gradient-to-r from-[#EF4444] to-[#8B5CF6]" />
            <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-[#F59E0B]">About Musicoul</p>
          </motion.div>
          <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-white mb-8 leading-tight">A Legacy of Musical<br />Excellence</motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-[#B0B0BE] font-light leading-[1.9] max-w-2xl mb-20 text-[15px]">
            Musicoul is a premier institution nurturing musical talent across genres. From Hindustani classical ragas to Western contemporary, our master faculty guides each student on a personalized journey.
          </motion.p>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20, scale: 0.95 }} whileInView={{ opacity: 1, y: 0, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="text-center py-6 sm:py-10 px-3 sm:px-4 bg-[#12121E] border border-[#8B5CF6]/8 rounded-xl hover:border-[#8B5CF6]/20 transition-all group hover:shadow-[0_4px_30px_rgba(139,92,246,0.08)]">
              <p className="font-heading text-2xl sm:text-4xl font-normal mb-2 sm:mb-3" style={{ color: s.color }}>{s.value}</p>
              <p className="text-[9px] sm:text-[11px] text-[#6B6B80] uppercase tracking-[0.15em] sm:tracking-[0.2em] group-hover:text-[#B0B0BE] transition-colors">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CoursesSection() {
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const navigate = useNavigate();
  useEffect(() => { axios.get(`${API}/courses`).then(r => setCourses(r.data)).catch((err) => console.error(err)); }, []);
  const categories = ['All', ...new Set(courses.map(c => c.category))];
  const filtered = courses.filter(c => { const ms = c.title?.toLowerCase().includes(search.toLowerCase()) || c.description?.toLowerCase().includes(search.toLowerCase()); return ms && (activeFilter === 'All' || c.category === activeFilter); });

  return (
    <section id="courses" className="py-16 sm:py-28 px-4 sm:px-5" data-testid="courses-section">
      <div className="max-w-6xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4"><div className="w-10 h-0.5 bg-gradient-to-r from-[#EF4444] to-[#8B5CF6]" /><p className="text-[11px] font-bold uppercase tracking-[0.35em] text-[#F59E0B]">Our Programs</p></motion.div>
          <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl font-normal text-white mb-10">Courses We Offer</motion.h2>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6B80]" />
              <input type="text" placeholder="Search courses..." value={search} onChange={e => setSearch(e.target.value)}
                className="w-full bg-[#12121E] border border-[#8B5CF6]/10 text-white py-3 pl-11 pr-4 text-sm focus:border-[#8B5CF6]/40 focus:outline-none focus:shadow-[0_0_15px_rgba(139,92,246,0.1)] transition-all placeholder:text-[#505068] rounded-xl"
                data-testid="course-search-input" />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {categories.map(cat => (
                <button key={cat} onClick={() => setActiveFilter(cat)}
                  className={`px-4 py-2.5 text-[12px] tracking-wider whitespace-nowrap transition-all rounded-xl ${activeFilter === cat
                    ? 'bg-gradient-to-r from-[#EF4444] to-[#8B5CF6] text-white font-bold shadow-[0_0_15px_rgba(139,92,246,0.25)]'
                    : 'bg-[#12121E] text-[#6B6B80] border border-[#8B5CF6]/8 hover:border-[#8B5CF6]/20'}`} data-testid={`filter-${cat}`}>{cat}</button>
              ))}
            </div>
          </div>
        </motion.div>
        <AnimatePresence mode="wait">
          <motion.div key={activeFilter + search} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((course, i) => (
              <motion.div key={course.course_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="group bg-[#12121E] border border-[#8B5CF6]/6 overflow-hidden cursor-pointer hover:border-[#8B5CF6]/20 transition-all duration-500 hover:shadow-[0_8px_40px_rgba(139,92,246,0.08)] rounded-xl"
                onClick={() => navigate(`/course/${course.course_id}`)} data-testid={`course-card-${course.course_id}`}>
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img loading="lazy" decoding="async" src={course.thumbnail} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#12121E] via-transparent to-transparent opacity-80" />
                  <div className="absolute top-3 left-3"><span className="bg-gradient-to-r from-[#EF4444] to-[#8B5CF6] px-3 py-1 text-[10px] text-white font-bold tracking-wider uppercase rounded-lg">{course.category}</span></div>
                </div>
                <div className="p-4 sm:p-5">
                  <h3 className="font-heading text-[15px] sm:text-[17px] text-white mb-1.5 sm:mb-2 group-hover:text-[#A78BFA] transition-colors">{course.title}</h3>
                  <p className="text-[12px] sm:text-[13px] text-[#6B6B80] mb-3 sm:mb-5 line-clamp-2">{course.description}</p>
                  <div className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-[11px] text-[#6B6B80] mb-3 sm:mb-5">
                    <span className="flex items-center gap-1"><Calendar size={10} className="text-[#F59E0B]" /> {course.schedule}</span>
                    <span className="flex items-center gap-1"><Clock size={10} className="text-[#8B5CF6]" /> {course.timing}</span>
                  </div>
                  <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-[#8B5CF6]/8">
                    <span className="text-white font-heading text-lg sm:text-2xl">&#8377;{course.price?.toLocaleString()}</span>
                    <span className="flex items-center gap-1 text-[10px] sm:text-[11px] text-[#6B6B80] group-hover:text-[#A78BFA] transition-colors">View <ArrowUpRight size={12} /></span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
        {filtered.length === 0 && <p className="text-center text-[#6B6B80] py-16 text-sm">No courses found.</p>}
      </div>
    </section>
  );
}

function LeaderboardPreview() {
  const [leaders, setLeaders] = useState([]);
  useEffect(() => { axios.get(`${API}/leaderboard?limit=5`).then(r => setLeaders(r.data)).catch((err) => console.error(err)); }, []);
  const medals = ['#EF4444', '#A78BFA', '#CD7F32'];
  return (
    <section id="leaderboard" className="py-16 sm:py-28 px-4 sm:px-5 bg-[#0E0E1A]" data-testid="leaderboard-preview">
      <div className="max-w-4xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4"><div className="w-10 h-0.5 bg-gradient-to-r from-[#EF4444] to-[#8B5CF6]" /><p className="text-[11px] font-bold uppercase tracking-[0.35em] text-[#F59E0B]">Rankings</p></motion.div>
          <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl font-normal text-white mb-12">Student Leaderboard</motion.h2>
        </motion.div>
        {leaders.length === 0 ? (
          <div className="text-center py-16 bg-[#12121E] border border-[#8B5CF6]/8 rounded-xl"><Trophy size={40} className="text-[#8B5CF6]/30 mx-auto mb-4" /><p className="text-[#6B6B80] text-sm">Be the first on the leaderboard!</p></div>
        ) : (
          <div className="space-y-2">{leaders.map((l, i) => (
            <motion.div key={l.user_id} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className={`flex items-center gap-4 p-4 rounded-xl transition-all ${i === 0 ? 'bg-gradient-to-r from-[#EF4444]/10 to-[#8B5CF6]/10 border border-[#8B5CF6]/20 shadow-[0_0_25px_rgba(139,92,246,0.08)]' : 'bg-[#12121E] border border-[#8B5CF6]/6'}`}>
              <div className="w-10 h-10 flex items-center justify-center font-heading text-lg font-bold rounded-lg" style={i < 3 ? { backgroundColor: `${medals[i]}18`, color: medals[i] } : { backgroundColor: '#1A1A28', color: '#6B6B80' }}>{i < 3 ? <Trophy size={18} /> : l.rank}</div>
              <div className="flex-1 min-w-0"><p className="text-white text-sm font-medium truncate">{l.name || 'Student'}</p></div>
              <p className="text-white font-heading text-xl">{l.total_points} <span className="text-[10px] text-[#6B6B80]">PTS</span></p>
            </motion.div>
          ))}</div>
        )}
      </div>
    </section>
  );
}

function CTASection() {
  const navigate = useNavigate();
  return (
    <section className="py-16 sm:py-32 px-4 sm:px-5 relative overflow-hidden" data-testid="cta-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(139,92,246,0.08)_0%,transparent_50%)]" />
      <div className="max-w-2xl mx-auto text-center relative z-10">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}><Sparkles size={24} className="text-[#8B5CF6] mx-auto mb-6" /></motion.div>
          <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-white mb-6">Begin Your Musical Journey</motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-[#B0B0BE] mb-12 text-[15px]">Join a community of passionate learners and transform your musical aspirations into artistry.</motion.p>
          <motion.button variants={fadeUp} custom={3} onClick={() => navigate('/auth')}
            className="bg-gradient-to-r from-[#EF4444] to-[#8B5CF6] text-white px-14 py-4 text-[13px] font-bold tracking-[0.2em] hover:shadow-[0_0_40px_rgba(139,92,246,0.4)] transition-all rounded-lg"
            data-testid="cta-enroll-btn">ENROLL TODAY</motion.button>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#8B5CF6]/8 py-10 px-5" data-testid="footer">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3"><img loading="lazy" decoding="async" src={LOGO} alt="" className="w-7 h-7 rounded-full" /><span className="font-heading text-sm tracking-[0.15em] text-white">MUSICOUL</span></div>
        <p className="text-[11px] text-[#505068] tracking-wider">&copy; {new Date().getFullYear()} Musicoul. All rights reserved.</p>
      </div>
    </footer>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Auto-redirect: logged-in users with active enrollments go straight to dashboard
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    axios.get(`${API}/enrollments`, { withCredentials: true })
      .then(r => {
        if (cancelled) return;
        const hasActive = (r.data?.enrollments || []).some(e => e.status === 'active');
        if (hasActive) navigate('/dashboard', { replace: true });
      })
      .catch((err) => console.error(err));
    return () => { cancelled = true; };
  }, [user, navigate]);

  return (
    <div className="bg-[#0A0A14] min-h-screen" data-testid="landing-page">
      <Header />
      <HeroSection />
      <AboutSection />
      <CoursesSection />
      <ShowcaseStrip />
      <LeaderboardPreview />
      <CTASection />
      <Footer />
      <div className="md:hidden h-20" />
      <div className="md:hidden"><BottomNav /></div>
    </div>
  );
}
